import React, { useState } from 'react';
import { styled } from '@material-ui/core/styles';
import { useLocation } from 'react-router-dom';
import {
  sidebarConfig,
  SidebarItem,
  SidebarSubmenu,
  SidebarSubmenuItem,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import { IconComponent } from '@backstage/core-plugin-api';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

export interface AccordionRoute {
  to: string;
  label: string;
  icon: IconComponent;
}

export interface SidebarAccordionItemProps {
  title: string;
  icon: IconComponent;
  routes: readonly AccordionRoute[];
}

const ToggleButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: 48,
  padding: 0,
  color: theme.palette.navigation.color,
  cursor: 'pointer',
  background: 'none',
  border: 'none',
  font: 'inherit',
  transition: 'background 0.15s ease',
  '&:hover': { background: theme.palette.navigation.navItem!.hoverBackground },
}));

ToggleButton.defaultProps = { type: 'button' };

const IconBox = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: sidebarConfig.iconContainerWidth,
  height: '100%',
  flexShrink: 0,
  marginRight: -16,
});

const Label = styled('span')(({ theme }) => ({
  flex: '1 1 auto',
  fontWeight: 700,
  fontSize: theme.typography.subtitle2.fontSize,
  color: theme.palette.navigation.color,
  whiteSpace: 'nowrap',
  textAlign: 'left',
}));

const Chevron = styled(ExpandMoreIcon)(({ theme }) => ({
  marginRight: theme.spacing(2),
  color: theme.palette.navigation.color,
  fontSize: 24,
  transition: 'transform 0.2s ease',
}));

const SubItems = styled('div')({
  borderLeft: '2px solid rgba(255,255,255,0.3)',
  marginLeft: 36,
  overflow: 'hidden',
});

const SubLink = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: 40,
  paddingLeft: theme.spacing(2),
  color: theme.palette.navigation.color,
  fontSize: 14,
  textDecoration: 'none',
  fontWeight: 700,
  transition: 'background 0.15s ease, color 0.15s ease',
  '&:hover': {
    background: theme.palette.navigation.navItem!.hoverBackground,
    color: theme.palette.navigation.selectedColor,
  },
  '&[data-active]': {
    color: theme.palette.navigation.selectedColor,
  },
}));

const Container = styled('div')({
  width: '224px',
});

export const SidebarAccordionItem = ({
  title,
  icon: Icon,
  routes,
}: SidebarAccordionItemProps) => {
  const { isOpen } = useSidebarOpenState();
  const { pathname } = useLocation();

  const isGroupActive = routes.some(({ to }) => pathname.startsWith(to));
  const [expanded, setExpanded] = useState(isGroupActive);

  if (!isOpen) {
    return (
      <SidebarItem icon={Icon} text={title} hasSubmenu>
        <SidebarSubmenu title={title}>
          {routes.map(({ to, label, icon }) => (
            <SidebarSubmenuItem key={to} title={label} to={to} icon={icon} />
          ))}
        </SidebarSubmenu>
      </SidebarItem>
    );
  }

  return (
    <Container>
      <ToggleButton
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
        data-active={isGroupActive ?? undefined}
      >
        <IconBox>
          <Icon fontSize="small" />
        </IconBox>
        <Label>{title}</Label>
        <Chevron
          style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </ToggleButton>

      {expanded && (
        <SubItems>
          {routes.map(({ to, label }) => (
            <SubLink
              key={to}
              to={to}
              underline="none"
              data-active={pathname.startsWith(to) ? '' : undefined}
            >
              {label}
            </SubLink>
          ))}
        </SubItems>
      )}
    </Container>
  );
};
